import { CaseType, CaseTypeRepository, MethodTypeRepository } from '@telespot/domain';
import { type CaseDataField } from '@protocols/domain';

/**
 * Flat, UI-ready projection of a case type for the case-type editor.
 *
 * `fields` is the entity's `CaseDataField[]` passed through as-is — the
 * repository's mapper already projects the persisted JSON into the union the
 * editor works in (and inverts it on save, so a save→reload round-trip is
 * stable). `methodTypes` resolves the assigned method types to `{ id, name }`
 * so the manager's right list can seed without a second round-trip.
 *
 * @group Protocols
 * @category Payloads
 */
export interface RetrieveCaseTypeOutput {
  id: string;
  name: string;
  description: string;
  fields: CaseDataField[];
  methodTypes: { id: string; name: string }[];
}

/**
 * Token-style error codes thrown by the retrieve-case-type query, mirroring the
 * documented command/query error pattern (a stable namespaced identifier
 * callers disambiguate without parsing free-form messages).
 */
export enum RetrieveCaseTypeErrorCode {
  CASE_TYPE_NOT_FOUND = 'retrieve_case_type.case_type_not_found',
  REPOSITORY_ERROR = 'retrieve_case_type.repository_error',
}

/**
 * Thrown when retrieving a case type fails. `details` carries structured
 * context (e.g. `{ caseTypeId }`) so callers can render/log without scraping
 * the message.
 */
export class RetrieveCaseTypeException extends Error {
  constructor(public readonly code: RetrieveCaseTypeErrorCode, public readonly details: Record<string, string> = {}) {
    super(code.toString());
    this.name = 'RetrieveCaseTypeException';
    Object.setPrototypeOf(this, RetrieveCaseTypeException.prototype);
  }
}

/**
 * Use case to retrieve a case type projected for the case-type editor.
 */
export interface IRetrieveCaseTypeQuery {
  /**
   * Retrieve the case type and resolve its assigned method types.
   * @param caseTypeId The id of the case type to retrieve.
   * @returns The case type as flat fields plus its data fields (mapped to the
   * `CaseDataField` union) and its assigned method types (`{ id, name }`).
   * @throws RetrieveCaseTypeException — a missing case type maps to
   * `CASE_TYPE_NOT_FOUND`; any repository failure (incl. resolving the assigned
   * method types, which are core editor data) maps to `REPOSITORY_ERROR`.
   */
  execute(caseTypeId: string): Promise<RetrieveCaseTypeOutput>;
}

export class RetrieveCaseTypeQuery implements IRetrieveCaseTypeQuery {
  constructor(
    private readonly caseTypeRepository: CaseTypeRepository,
    private readonly methodTypeRepository: MethodTypeRepository
  ) {}

  public async execute(caseTypeId: string): Promise<RetrieveCaseTypeOutput> {
    const caseType = await this.getCaseType(caseTypeId);
    const methodTypes = await this.resolveAssignedMethodTypes(caseTypeId);

    return {
      id: caseType.id,
      name: caseType.name,
      description: caseType.description ?? '',
      fields: caseType.fields ?? [],
      methodTypes,
    };
  }

  private async getCaseType(caseTypeId: string): Promise<CaseType> {
    let caseType: CaseType | undefined;
    try {
      caseType = await this.caseTypeRepository.getById(caseTypeId);
    } catch (error) {
      throw this.toException(error, caseTypeId);
    }
    if (!caseType) {
      throw new RetrieveCaseTypeException(RetrieveCaseTypeErrorCode.CASE_TYPE_NOT_FOUND, { caseTypeId });
    }
    return caseType;
  }

  /**
   * The assigned method types are core editor data (unlike the method-type
   * query's best-effort asset *name* lookup), so a failure here fails the query.
   */
  private async resolveAssignedMethodTypes(caseTypeId: string): Promise<{ id: string; name: string }[]> {
    try {
      const methodTypes = await this.methodTypeRepository.findForCaseType(caseTypeId);
      return methodTypes.map((methodType) => ({ id: methodType.id, name: methodType.name }));
    } catch {
      throw new RetrieveCaseTypeException(RetrieveCaseTypeErrorCode.REPOSITORY_ERROR, { caseTypeId });
    }
  }

  private toException(error: unknown, caseTypeId: string): RetrieveCaseTypeException {
    if (this.isNotFoundError(error)) {
      return new RetrieveCaseTypeException(RetrieveCaseTypeErrorCode.CASE_TYPE_NOT_FOUND, { caseTypeId });
    }
    return new RetrieveCaseTypeException(RetrieveCaseTypeErrorCode.REPOSITORY_ERROR, { caseTypeId });
  }

  private isNotFoundError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /object not found/i.test(error.message);
  }
}
